"use client";
import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { cn } from "../../lib/utils";

export const BoxesCore = ({ className, ...rest }: { className?: string }) => {
  const rows = new Array(100).fill(1);
  const cols = new Array(60).fill(1);
  const [activeBoxes, setActiveBoxes] = useState<Map<string, string>>(new Map());
  
  const colors = useMemo(() => [
    "#7DD3FC", // sky-300
    "#F9A8D4", // pink-300
    "#86EFAC", // green-300
    "#FDE047", // yellow-300
    "#FCA5A5", // red-300
    "#D8B4FE", // purple-300
    "#93C5FD", // blue-300
    "#A5B4FC", // indigo-300
    "#C4B5FD", // violet-300
  ], []);

  const getRandomColor = () => colors[Math.floor(Math.random() * colors.length)];

  useEffect(() => {
    let animationFrameId: number;
    let lastUpdate = 0;
    const updateInterval = 600; // Slightly faster updates

    const createCluster = (centerRow: number, centerCol: number, size: number) => {
      const cluster = new Map<string, string>();
      const color = getRandomColor();
      const radius = Math.floor(Math.random() * 2) + 1; // Random radius between 1-2

      // Create a small cluster of 3-5 boxes
      for (let i = 0; i < size; i++) {
        const angle = (Math.PI * 2 * i) / size;
        const r = radius * Math.random(); // Random distance within radius
        const offsetRow = Math.round(Math.cos(angle) * r);
        const offsetCol = Math.round(Math.sin(angle) * r);
        
        const newRow = centerRow + offsetRow;
        const newCol = centerCol + offsetCol;

        if (
          newRow >= 0 && newRow < rows.length &&
          newCol >= 0 && newCol < cols.length
        ) {
          cluster.set(`${newRow}-${newCol}`, color);
        }
      }

      return cluster;
    };

    const createPattern = () => {
      const now = Date.now();
      if (now - lastUpdate < updateInterval) {
        animationFrameId = requestAnimationFrame(createPattern);
        return;
      }
      lastUpdate = now;

      const newActiveBoxes = new Map<string, string>();

      // Create 1-2 clusters of 3-5 boxes
      const numClusters = Math.floor(Math.random() * 2) + 1;
      for (let i = 0; i < numClusters; i++) {
        const centerRow = Math.floor(Math.random() * rows.length);
        const centerCol = Math.floor(Math.random() * cols.length);
        const clusterSize = Math.floor(Math.random() * 3) + 3; // Random size between 3-5
        
        const cluster = createCluster(centerRow, centerCol, clusterSize);
        cluster.forEach((value, key) => newActiveBoxes.set(key, value));
      }

      // Add 2-3 random individual boxes
      const numRandomBoxes = Math.floor(Math.random() * 2) + 2;
      for (let i = 0; i < numRandomBoxes; i++) {
        const row = Math.floor(Math.random() * rows.length);
        const col = Math.floor(Math.random() * cols.length);
        newActiveBoxes.set(`${row}-${col}`, getRandomColor());
      }

      setActiveBoxes(prev => {
        const merged = new Map(prev);
        newActiveBoxes.forEach((value, key) => {
          merged.set(key, value);
        });
        // Keep only the most recent animations
        if (merged.size > 40) {
          const keys = Array.from(merged.keys());
          for (let i = 0; i < merged.size - 40; i++) {
            merged.delete(keys[i]);
          }
        }
        return merged;
      });

      animationFrameId = requestAnimationFrame(createPattern);
    };

    createPattern();
    return () => cancelAnimationFrame(animationFrameId);
  }, [rows.length, cols.length, colors]);

  const gridStyle = useMemo(() => ({
    transform: `translate(-40%,-60%) skewX(-48deg) skewY(14deg) scale(0.675) rotate(0deg) translateZ(0)`,
  }), []);

  return (
    <div
      style={gridStyle}
      className={cn(
        "absolute left-1/4 p-4 -top-1/4 flex -translate-x-1/2 -translate-y-1/2 w-full h-full z-0",
        className
      )}
      {...rest}
    >
      {rows.map((_, i) => (
        <motion.div
          key={`row-${i}`}
          className="w-16 h-8 border-l border-slate-700/30 relative"
        >
          {cols.map((_, j) => {
            const cellId = `${i}-${j}`;
            const activeColor = activeBoxes.get(cellId);
            
            return (
              <motion.div
                key={`col-${j}`}
                className="w-16 h-8 border-r border-t border-slate-700/30 relative"
                animate={activeColor ? {
                  backgroundColor: activeColor,
                  scale: 1.2,
                } : {
                  backgroundColor: "transparent",
                  scale: 1,
                }}
                transition={{
                  duration: 0.4,
                  ease: "easeOut",
                }}
              />
            );
          })}
        </motion.div>
      ))}
    </div>
  );
};

export const Boxes = React.memo(BoxesCore);

interface BackgroundBoxesProps {
  className?: string;
  children?: React.ReactNode;
}

export function BackgroundBoxes({ className, children }: BackgroundBoxesProps) {
  return (
    <div 
      className={cn("relative w-full overflow-hidden bg-slate-950", className)}
    >
      <div className="absolute inset-0 z-0">
        <Boxes />
      </div>
      {children && (
        <div className="relative z-10">
          {children}
        </div>
      )}
    </div>
  );
}